import { useMemo } from 'react'

import { UserRole } from '@/organizations/schemas'
import type { GetOrganizationDirectoryUsersResponse, GetOrganizationWithDetailsResponse } from '@/organizations/schemas'
import type { MemberRosterMember } from '@/organizations/views/MemberRosterModal'
import { joinNameParts } from '@/util/string-utils'

interface UseRosterMembersParams {
  isOrgAdmin: boolean
  orgDetails: GetOrganizationWithDetailsResponse | undefined
  directoryUsers: GetOrganizationDirectoryUsersResponse | undefined
}

/**
 * Shapes organization members into the roster modal's member list, sorted admins-first.
 *
 * Admins receive full details (active users + pending invites); non-admins receive the
 * member-visible directory listing. Returns a stable, memoized array.
 */
export function useRosterMembers({ isOrgAdmin, orgDetails, directoryUsers }: UseRosterMembersParams): MemberRosterMember[] {
  return useMemo(() => {
    if (isOrgAdmin) {
      const activeMembers: MemberRosterMember[] = [...(orgDetails?.organization.users ?? [])]
        .sort((a, b) => (a.role === UserRole.ADMIN ? 0 : 1) - (b.role === UserRole.ADMIN ? 0 : 1))
        .map((u) => ({
          id: u.firebase_id ?? u.id,
          displayName: joinNameParts(u.first_name, u.last_name),
          email: u.email,
          role: u.role,
          isPending: false,
          userId: u.id,
          isOrgPayer: u.is_organization_payer,
        }))

      const pendingInvites: MemberRosterMember[] = (orgDetails?.organization.invites ?? []).map((inv) => ({
        id: inv.id,
        displayName: null,
        email: inv.invitee_email,
        role: null,
        isPending: true,
      }))

      return [...activeMembers, ...pendingInvites]
    }

    return [...(directoryUsers?.users ?? [])]
      .sort((a, b) => (a.org_role === UserRole.ADMIN ? 0 : 1) - (b.org_role === UserRole.ADMIN ? 0 : 1))
      .map((u) => ({
        id: u.user_id,
        displayName: u.display_name,
        email: u.email ?? '',
        role: u.org_role,
        isPending: false,
      }))
  }, [isOrgAdmin, orgDetails, directoryUsers])
}
